import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { BottomSheetPageContainer } from "@/components/container/bottom-sheet-page-container";
import SearchBar from "@/components/form/hook-form/search-bar";
import { BackIcon } from "@/components/icons/back-icon";
import { useSearchMeals } from "@/hooks/requests/meal/use-search-meals";
import { useGetSearchTags } from "@/hooks/requests/plan/use-get-search-tags";
import { useIonRouter } from "@ionic/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { AlternativeMealsList } from "../../components/alternative-meals-list";
import { SearchActionButton } from "../../components/search-action-button";
import { SearchTags } from "../../components/search-tags";
import { getMealTimePageTitle } from "../../utils/get-meal-time-page-title";

interface SearchFormValues {
  search: string;
}

interface SwapPlanSearchPageProps {
  mealData: Meal | null;
  mealTime: MealTime | undefined;
  mealDay: string | undefined;
  mealIndex: number | undefined;
  onClose: () => void;
}

export default function SwapPlanSearchPage({
  mealData,
  mealTime,
  mealDay,
  mealIndex,
  onClose,
}: SwapPlanSearchPageProps) {
  const navigation = useIonRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);

  const { control, watch, setValue } = useForm<SearchFormValues>({
    defaultValues: {
      search: "",
    },
  });

  const searchValue = watch("search");
  const { searchTags, isLoading: tagsLoading } = useGetSearchTags(mealTime ?? "");

  // Search meals when user submits a query
  const {
    meals,
    isLoading: mealsLoading,
    isFetching,
  } = useSearchMeals({
    query: searchQuery,
    mealTime,
    enabled: showResults && !!searchQuery.trim(),
  });

  const alternativeMeals = useMemo(() => meals, [meals]);

  const handleTagClick = useCallback(
    (tag: string) => {
      setValue("search", tag);
      setSearchQuery(tag);
      setShowResults(true);
    },
    [setValue],
  );

  const handleSearch = useCallback(() => {
    setSearchQuery(searchValue);
    setShowResults(true);
  }, [searchValue]);

  const handleClearSearch = useCallback(() => {
    setValue("search", "");
    setSearchQuery("");
    setShowResults(false);
  }, [setValue]);

  const handleBackClick = useCallback(() => {
    if (searchValue || showResults) {
      // Clear the search form if there's a search value or results are showing
      handleClearSearch();
    } else {
      // Otherwise, navigate back
      navigation.goBack();
    }
  }, [searchValue, showResults, handleClearSearch, navigation]);

  // Reset to SearchActionButton when search input changes while showing results
  useEffect(() => {
    if (showResults && searchValue !== searchQuery) {
      setShowResults(false);
    }
  }, [searchValue, searchQuery, showResults]);

  if (!mealData || !mealTime || !mealDay) {
    return null;
  }

  return (
    <BottomSheetPageContainer
      showToolbar
      title={getMealTimePageTitle(mealTime)}
      bgColor="white"
      headerBgColor="white"
      contentBgColor="white"
      className="gap-0"
      onBack={handleBackClick}
      backIcon={searchValue || showResults ? <BackIcon /> : undefined}
      backButtonAriaLabel={searchValue || showResults ? "Clear search" : "Close"}
    >
      <div className="flex h-full w-full flex-col px-4 pt-2">
        <SearchBar
          control={control}
          name="search"
          placeholder="Chicken"
          className="w-full"
          aria-label="Search meals"
        />

        <div className="flex-1 gap-3.5 overflow-y-auto px-4">
          {searchValue ? (
            showResults ? (
              <AlternativeMealsList
                meals={alternativeMeals}
                mealTime={mealTime}
                mealDay={mealDay}
                mealIndex={mealIndex}
                currentMealId={mealData?.id ?? undefined}
                requestId={null}
                isLoading={mealsLoading}
                isFetching={isFetching}
                onClearSearch={handleClearSearch}
                onComplete={onClose}
              />
            ) : (
              <SearchActionButton onSearch={handleSearch} />
            )
          ) : (
            <SearchTags tags={searchTags} isLoading={tagsLoading} onTagClick={handleTagClick} />
          )}
        </div>
      </div>
    </BottomSheetPageContainer>
  );
}
